import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { RoutePaths } from "@/core/constants/RoutePaths";
import { useUserSector } from "@/core/hooks/useUserSector";
import { useSectorAccessState } from "@/core/hooks/useSectorAccessState";
import { resolveBillingGate } from "@/features/billing/utils/resolveBillingGate";

type Props = { children: ReactNode };

/**
 * Guarda as rotas do produto (dentro do MainLayout). Não renderiza nenhuma tela
 * de bloqueio inline — quando o billing trava o acesso, **redireciona** para a
 * página dedicada de gate (`PLAN_GATE`), que ocupa a tela inteira. Assim o
 * paywall/onboarding não fica espremido no conteúdo ao lado do header/sidebar.
 */
export function SectorAccessGuard({ children }: Props) {
  const { activeSectorId } = useUserSector();
  const { data: access, isSuccess } = useSectorAccessState(activeSectorId);

  // Só decidimos o gate com o access-state REALMENTE carregado para o sector
  // ativo. Enquanto não houver sector resolvido ou o fetch não tiver concluído,
  // libera o conteúdo (fail-open) SEM redirecionar — redirecionar no estado
  // transitório (sector ainda nulo → fail-open) causaria ping-pong com a
  // página de gate.
  if (!activeSectorId || !isSuccess) {
    return <>{children}</>;
  }

  const gate = resolveBillingGate(access);
  if (gate.kind !== "allow") {
    return (
      <Navigate
        to={RoutePaths.PLAN_GATE}
        replace
      />
    );
  }

  return <>{children}</>;
}
